/**
 * The Elevate content governance surface: row shapes and the derivations the
 * back-office screens share.
 *
 * Split from elevateGovernanceApi.ts on purpose. Everything here is pure, so
 * the governance tests can assert on the counting rules without a configured
 * Supabase client standing between them and the assertion.
 */

// =====================================================================
// Row shapes — the RPC RETURNS TABLE column lists, verbatim
// =====================================================================

/** PRS 8.3 lifecycle states, as migration 235 constrains the column. */
export type ContentLifecycleStatus =
  | 'draft'
  | 'technical_review'
  | 'educational_review'
  | 'awaiting_approval'
  | 'approved'
  | 'published'
  | 'under_review'
  | 'superseded'
  | 'archived';

export interface ReviewQueueRow {
  object_uuid: string;
  object_id: string;
  title: string;
  description: string;
  pack_uuid: string;
  content_pack_id: string;
  pack_title: string;
  authored_created_at: string;
  competency_code: string;
  development_level: string;
  object_type: 'core' | 'supplement';
  format: string;
  ksa_dimension: string;
  observable_behaviour_codes: string[];
  duration_minutes: number;
  mandatory: boolean;
  language: string;
  owner: string;
  author: string;
  version: string;
  status: ContentLifecycleStatus;
  /** GOV-07 marker. Platform-set; never an ingestion field. */
  ai_authored: boolean;
  /** GOV-09 marker. The badge the governance screens must show. */
  test_only: boolean;
  created_at: string;
  approved_version: string | null;
  approver_name: string | null;
  approver_email: string | null;
  approved_at: string | null;
  has_current_approval: boolean;
  /** Advisory: the guard is what actually refuses. */
  can_approve: boolean;
  can_publish: boolean;
}

export interface PublicationVerificationRow {
  competency_code: string;
  development_level: string;
  object_id: string;
  title: string;
  object_type: 'core' | 'supplement';
  version: string;
  test_only: boolean;
  ai_authored: boolean;
  content_pack_id: string;
  authored_created_at: string;
  authored_framework_version: string;
  approver_name: string;
  approver_email: string;
  approved_at: string;
  approved_version: string;
}

export interface GateCountRow {
  competency_code: string;
  competency_title: string;
  listing_position: number | null;
  published_variants: number;
  published_core: number;
  published_supplements: number;
  /** Counted apart and never inside the gate total (GOV-09). */
  test_only_variants: number;
  levels_covered: number;
  first_published_at: string | null;
  last_approved_at: string | null;
}

export interface GateTargetRow {
  gate: string;
  milestone_date: string;
  variants: number;
  source: string;
}

export interface ContentApproverRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  granted_by: string;
  granted_at: string;
  note: string | null;
}

export interface GovernanceActionResult {
  object_uuid: string;
  object_id: string;
  content_version: string;
  status_before: ContentLifecycleStatus;
  status_after: ContentLifecycleStatus;
  approver_name?: string;
  approver_email?: string;
  approved_at?: string;
  test_only?: boolean;
}

// =====================================================================
// Derivations the screens share (and the tests assert on directly)
// =====================================================================

/** The queue grouped by pack, packs in the order the RPC returned them. */
export function groupQueueByPack(rows: ReviewQueueRow[]): Array<{
  contentPackId: string;
  packTitle: string;
  authoredCreatedAt: string;
  objects: ReviewQueueRow[];
}> {
  const order: string[] = [];
  const packs = new Map<string, ReviewQueueRow[]>();
  for (const row of rows) {
    if (!packs.has(row.content_pack_id)) {
      packs.set(row.content_pack_id, []);
      order.push(row.content_pack_id);
    }
    packs.get(row.content_pack_id)!.push(row);
  }
  return order.map((id) => {
    const objects = packs.get(id) ?? [];
    const first = objects[0];
    return {
      contentPackId: id,
      packTitle: first?.pack_title ?? id,
      authoredCreatedAt: first?.authored_created_at ?? '',
      objects,
    };
  });
}

/**
 * Gate progress. The published total deliberately excludes test-only variants:
 * GOV-09 keeps that material out of any production release, so counting it
 * towards a launch-content gate would overstate readiness by exactly the amount
 * that must not ship. The database applies the same rule; this is its display.
 */
export function summariseGateProgress(
  counts: GateCountRow[],
  targets: GateTargetRow[],
): Array<{ gate: string; milestoneDate: string; target: number; published: number; met: boolean; source: string }> {
  const published = counts.reduce((total, row) => total + Number(row.published_variants), 0);
  return targets.map((t) => ({
    gate: t.gate,
    milestoneDate: t.milestone_date,
    target: t.variants,
    published,
    met: published >= t.variants,
    source: t.source,
  }));
}

/**
 * S5-05, the governance half. The database keeps test-only material away from a
 * production tenant's trainees; the person approving and publishing it needs the
 * opposite — to find it deliberately and see how much of it there is.
 */
export function summariseTestOnlyExposure(rows: ReviewQueueRow[]): {
  total: number;
  testOnly: number;
  visible: (onlyTestOnly: boolean) => ReviewQueueRow[];
} {
  return {
    total: rows.length,
    testOnly: rows.filter((r) => r.test_only).length,
    visible: (onlyTestOnly: boolean) => rows.filter((r) => !onlyTestOnly || r.test_only),
  };
}

/** Human label for a PRS 8.3 state. Back-office vocabulary, not trainee copy. */
export const LIFECYCLE_LABELS: Record<ContentLifecycleStatus, string> = {
  draft: 'Draft',
  technical_review: 'Technical review',
  educational_review: 'Educational review',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  published: 'Published',
  under_review: 'Under review',
  superseded: 'Superseded',
  archived: 'Archived',
};

export const DEVELOPMENT_LEVEL_LABELS: Record<string, string> = {
  beginner: 'Beginner',
  building_competencies: 'Building competencies',
  proficient: 'Proficient',
};
